import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Eye, Radio, VideoOff } from "lucide-react";
import { useSessionWatchData } from "@/hooks/useSessionWatchData";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { apiClient } from "@/services/api";
import { extractPlaybackToken } from "@/utils/playbackToken";
import { WatchPlayer } from "@/components/watch/WatchPlayer";
import { AngleSwitcher } from "@/components/watch/AngleSwitcher";
import { ShareMenu } from "@/components/watch/ShareMenu";
import { LiveStreamPlayer } from "@/components/liveStream/LiveStreamPlayer";
import { StatusCard, LoadingRow } from "@/components/watch/StatusCards";
import { formatDateShort } from "@/utils/dateFormat";
import type { RecordingPlaybackStatus } from "@/types";

// Auto-advance (WatchPlayer's onEnded) and the AngleSwitcher only ever hand
// back a fragment whose group already vetted it as playable, but the search
// here re-derives "playable" from the raw fragment list, so it needs the same
// definition useSessionWatchData uses internally.
const PLAYABLE_STATUSES = new Set<RecordingPlaybackStatus>(["ready", "partial"]);

/**
 * Public, unauthenticated `/watch/:sessionId` page: a standalone, video-first
 * viewer for sharing a session outside the app. Rendered outside the app
 * Layout (see routesConfig.ts) on a full-height dark ground.
 *
 * `useSessionWatchData` owns every data concern (session metadata, live
 * status, recordings/angle grouping, and the derived page state); this
 * component only decides what to render for each state:
 *  - `live`: unchanged from before this page understood VOD — the immersive
 *    `LiveStreamPlayer` owns the entire live experience.
 *  - `vod`: a classic ~960px centered column (edge-to-edge player on mobile)
 *    with `WatchPlayer` + `AngleSwitcher`.
 *  - `ended` / `processing` / `empty` / `unavailable`: simple centered message cards.
 */
export default function WatchPage() {
  const { sessionId = "" } = useParams();
  const { t } = useTranslation("pages");
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const {
    session,
    pageState,
    angleGroups,
    streamData,
    refetchRecordings,
    getPlayableFragment,
    isLoading,
  } = useSessionWatchData(sessionId);
  usePageMeta({ title: session?.title, description: session?.description });

  // Whether the currently selected fragment should skip WatchPlayer's
  // lazy-activation placeholder. False on mount (including a ?r= deep link —
  // no gesture has happened yet); flipped permanently to true the first time
  // the viewer switches angles or a fragment auto-advances, since both of
  // those already carry an implicit "keep playing" gesture.
  const [hasInteracted, setHasInteracted] = useState(false);

  // `?r=` is the single source of truth for the selection — no separate
  // local "selected fragment" state to keep in sync. getPlayableFragment
  // already implements the fallback contract: a missing/invalid/unplayable
  // `r` resolves to the first playable fragment across angle groups.
  const rParam = searchParams.get("r");
  const rawT = searchParams.get("t");
  const initialTime = useMemo(() => {
    if (!rawT) return null;
    const n = Number(rawT);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }, [rawT]);

  const [liveTime, setLiveTime] = useState(0);
  const currentFragment = useMemo(
    () => getPlayableFragment(rParam ?? undefined),
    [rParam, getPlayableFragment],
  );

  const activeGroup = useMemo(() => {
    if (!currentFragment) return undefined;
    return angleGroups.find((group) =>
      group.fragments.some((fragment) => fragment.id === currentFragment.id),
    );
  }, [angleGroups, currentFragment]);

  const partLabel = useMemo(() => {
    if (!activeGroup || !currentFragment || activeGroup.fragments.length <= 1) {
      return null;
    }
    const index = activeGroup.fragments.findIndex(
      (fragment) => fragment.id === currentFragment.id,
    );
    if (index === -1) return null;
    return t("watch.partLabel", {
      ns: "pages",
      i: index + 1,
      total: activeGroup.fragments.length,
    });
  }, [activeGroup, currentFragment, t]);

  // Shared by both the AngleSwitcher's click and fragment auto-advance: both
  // are a "keep playing" gesture, so both mark the page as interacted and
  // sync `?r=` without pushing a new history entry.
  const selectFragment = useCallback(
    (fragmentId: string) => {
      setHasInteracted(true);
      setSearchParams({ r: fragmentId }, { replace: true });
    },
    [setSearchParams],
  );

  const handleEnded = useCallback(() => {
    if (!currentFragment || !activeGroup) return;
    const currentIndex = activeGroup.fragments.findIndex(
      (fragment) => fragment.id === currentFragment.id,
    );
    const next = activeGroup.fragments
      .slice(currentIndex + 1)
      .find((fragment) => PLAYABLE_STATUSES.has(fragment.playbackStatus));
    if (next) selectFragment(next.id);
  }, [activeGroup, currentFragment, selectFragment]);

  const handlePlaybackAuthError = useCallback(() => {
    refetchRecordings();
  }, [refetchRecordings]);

  // Count views on playback start (issue #649). Authenticated viewers
  // dedup by account; anonymous viewers present the signed playback token
  // (proof they could actually play this recording) and dedup by a
  // server-side fingerprint. Without either credential, skip silently —
  // no point 401-noising the console.
  const handlePlaybackStart = useCallback(
    (fragmentId: string) => {
      const fragment = angleGroups
        .flatMap((group) => group.fragments)
        .find((fragment) => fragment.id === fragmentId);
      const token = extractPlaybackToken(fragment?.playbackUrl ?? null);
      if (!user && !token) return;
      apiClient
        .recordRecordingView(fragmentId, token ?? undefined)
        .catch((error) => {
          console.warn("Failed to record view:", error);
        });
    },
    [angleGroups, user],
  );

  // Gate on isLoading before touching pageState: pageState has no "loading"
  // member by design, so branching on it first would flash the "empty" card
  // during the initial fetch.
  if (isLoading) {
    return (
      <div className="flex h-dvh min-h-screen w-full flex-col bg-black">
        <LoadingRow label={t("watch.loading", { ns: "pages" })} />
      </div>
    );
  }

  // The classic-column VOD composition needs its own (scrollable, not
  // viewport-locked) layout, so it gets an early return rather than sharing
  // the centered-flex wrapper used by every other state below.
  if (pageState === "vod" && currentFragment) {
    return (
      <div className="min-h-screen w-full bg-black">
        <div className="mx-auto w-full max-w-[960px] pb-16 pt-6 sm:px-5">
          <WatchPlayer
            fragment={currentFragment}
            partLabel={partLabel}
            onEnded={handleEnded}
            onPlaybackAuthError={handlePlaybackAuthError}
            onPlaybackStart={handlePlaybackStart}
            autoActivate={hasInteracted || initialTime !== null}
            initialTime={initialTime}
            onTimeUpdate={setLiveTime}
            className="max-sm:rounded-none"
          />
          <div className="px-4 pt-4 sm:px-0">
            {session && (
              <>
                <h1 className="text-lg font-semibold text-white">{session.title}</h1>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-white/60">
                  <span>{formatDateShort(session.conductedAt)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{session.event.track.name}</span>
                  {typeof currentFragment.viewCount === "number" && (
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>
                        {t("watch.viewCount", {
                          count: currentFragment.viewCount,
                        })}
                      </span>
                    </span>
                  )}
                  {session.visibility === "public" && (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">
                      {t("watch.public", { ns: "pages" })}
                    </span>
                  )}
                </div>
              </>
            )}
            <AngleSwitcher
              groups={angleGroups}
              activeFragmentId={currentFragment.id}
              onSelectFragment={selectFragment}
            />
            <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-white/40">
              <span>{t("watch.hostedByRaceManager", { ns: "pages" })}</span>
              <ShareMenu currentTime={liveTime} fragmentId={currentFragment.id} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  let content: ReactNode;
  if (pageState === "unavailable") {
    content = (
      <StatusCard
        icon={<AlertCircle className="h-6 w-6" />}
        title={t("watch.unavailable.title", { ns: "pages" })}
        body={t("watch.unavailable.body", { ns: "pages" })}
        hint={t("watch.unavailable.hint", { ns: "pages" })}
      />
    );
  } else if (pageState === "live" && streamData?.whepUrl) {
    content = (
      <LiveStreamPlayer
        variant="immersive"
        src={streamData.whepUrl}
        title={streamData.title}
        startTime={streamData.startedAt}
        isRunning={streamData.displayStatus === "live"}
        shareUrl={typeof window !== "undefined" ? window.location.href : undefined}
      />
    );
  } else if (pageState === "processing") {
    content = <LoadingRow label={t("watch.processing", { ns: "pages" })} />;
  } else if (pageState === "vod") {
    // Defensive: pageState says a playable recording exists, but the
    // resolver came back empty (e.g. angle groups changed shape between
    // renders). Should not happen in practice; render a plain message
    // rather than crash on a missing fragment.
    content = (
      <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-white/60">
        {t("watch.noVideo", { ns: "pages" })}
      </div>
    );
  } else if (pageState === "ended") {
    content = (
      <StatusCard
        icon={<VideoOff className="h-6 w-6" />}
        title={t("watch.ended.title", { ns: "pages" })}
        body={t("watch.ended.body", { ns: "pages" })}
      />
    );
  } else {
    content = (
      <StatusCard
        icon={<Radio className="h-6 w-6" />}
        title={t("watch.notLive.title", { ns: "pages" })}
        body={t("watch.notLive.body", { ns: "pages" })}
      />
    );
  }

  return <div className="flex h-dvh min-h-screen w-full flex-col bg-black">{content}</div>;
}
